import { useState } from "react";
import { motion } from "framer-motion";
import { TRIAL } from "@/constants/testIds";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";

const WA_URL =
    "https://wa.me/919717622852?text=" +
    encodeURIComponent("Hi, I want to book my $49 Aestherium Media trial week.");

export default function TrialBooking() {
    const [form, setForm] = useState({
        name: "",
        business: "",
        ig_handle: "",
        email: "",
        message: "",
    });
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/leads", form);
            setSubmitted(true);
            toast.success("Lead captured. Continuing to WhatsApp…");
            setTimeout(() => {
                window.open(WA_URL, "_blank", "noopener");
            }, 800);
        } catch (err) {
            toast.error(formatApiError(err, "Could not submit. Please try WhatsApp directly."));
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            id="trial"
            data-testid={TRIAL.root}
            className="relative py-24 md:py-32"
        >
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9 }}
                    className="relative rounded-3xl surface-3d p-8 md:p-14 lg:p-16 overflow-hidden"
                >
                    <div className="absolute inset-0 grain pointer-events-none opacity-40" />
                    <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
                        <div className="lg:col-span-6">
                            <p className="text-xs uppercase tracking-[0.35em] text-[var(--accent-gold)] mb-5">
                                The Trial
                            </p>
                            <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.02] text-[var(--text-primary)]">
                                Your Brand Deserves To{" "}
                                <span className="italic text-[var(--accent-champagne)]">
                                    Dominate.
                                </span>
                            </h2>
                            <p className="mt-6 text-lg text-[var(--text-secondary)] max-w-lg">
                                You already have something worth growing. Give it the system that
                                turns reach into revenue. Try us for the price of a coffee.{" "}
                                <span className="gold-shimmer font-semibold">$49</span>, one week,
                                the real work.
                            </p>

                            <ul className="mt-8 space-y-3 text-[var(--text-secondary)]">
                                {[
                                    "No card details on this site",
                                    "Weekly billing after trial. No long contracts",
                                    "Audit + strategy within 24 hours",
                                ].map((li) => (
                                    <li key={li} className="flex items-center gap-3">
                                        <CheckCircle2
                                            size={18}
                                            className="text-[var(--accent-gold)] shrink-0"
                                        />
                                        <span>{li}</span>
                                    </li>
                                ))}
                            </ul>

                            <a
                                data-testid={TRIAL.whatsappBtn}
                                href={WA_URL}
                                target="_blank"
                                rel="noreferrer"
                                className="mt-10 inline-flex items-center gap-2 text-sm text-[var(--text-primary)] hover:text-[var(--accent-champagne)] transition-colors underline underline-offset-4 decoration-[var(--accent-gold)]/40"
                            >
                                Prefer WhatsApp? Message us directly
                                <ArrowUpRight size={14} />
                            </a>
                        </div>

                        <div className="lg:col-span-6">
                            <form
                                data-testid={TRIAL.form}
                                onSubmit={submit}
                                className="rounded-2xl bg-[rgba(0,0,0,0.35)] border border-[var(--border-subtle)] p-6 md:p-7 space-y-4 backdrop-blur-sm"
                            >
                                <Field
                                    label="Your Name"
                                    testid={TRIAL.inputName}
                                    value={form.name}
                                    onChange={onChange("name")}
                                    required
                                    disabled={submitted}
                                />
                                <Field
                                    label="Business / Brand"
                                    testid={TRIAL.inputBusiness}
                                    value={form.business}
                                    onChange={onChange("business")}
                                    required
                                    disabled={submitted}
                                />
                                <Field
                                    label="Instagram Handle"
                                    testid={TRIAL.inputIg}
                                    value={form.ig_handle}
                                    onChange={onChange("ig_handle")}
                                    placeholder="@yourbrand"
                                    required
                                    disabled={submitted}
                                />
                                <Field
                                    label="Email"
                                    testid={TRIAL.inputEmail}
                                    value={form.email}
                                    onChange={onChange("email")}
                                    type="email"
                                    required
                                    disabled={submitted}
                                />
                                <div>
                                    <label className="block text-[11px] uppercase tracking-[0.25em] text-[var(--accent-champagne)]/70 mb-2">
                                        Anything we should know (optional)
                                    </label>
                                    <textarea
                                        data-testid={TRIAL.inputMessage}
                                        value={form.message}
                                        onChange={onChange("message")}
                                        rows={3}
                                        disabled={submitted}
                                        className="w-full bg-transparent border border-[var(--border-subtle)] rounded-xl px-4 py-3 text-[var(--text-primary)] placeholder:text-[var(--text-secondary)]/40 focus:outline-none focus:border-[var(--accent-gold)] transition-colors"
                                        placeholder="Current goals, biggest bottleneck…"
                                    />
                                </div>
                                <button
                                    data-testid={TRIAL.submit}
                                    type="submit"
                                    disabled={loading || submitted}
                                    className="w-full inline-flex items-center justify-center gap-2 py-4 rounded-full bg-[var(--accent-gold)] text-[var(--brand-dark)] font-semibold uppercase tracking-[0.2em] text-sm hover:bg-[var(--accent-champagne)] transition-all disabled:opacity-70 disabled:cursor-not-allowed shadow-[0_20px_40px_-15px_rgba(212,175,55,0.6)]"
                                >
                                    {submitted
                                        ? "Redirecting to WhatsApp…"
                                        : loading
                                          ? "Sending…"
                                          : "Book My $49 Trial"}
                                    <ArrowUpRight size={16} />
                                </button>
                                <p className="text-[11px] text-[var(--text-secondary)]/60 text-center leading-relaxed">
                                    By submitting, you agree we will reach out on the info above.
                                    No card details are collected on this site.
                                </p>
                            </form>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}

function Field({ label, testid, ...props }) {
    return (
        <div>
            <label className="block text-[11px] uppercase tracking-[0.25em] text-[var(--accent-champagne)]/70 mb-2">
                {label}
            </label>
            <input
                data-testid={testid}
                {...props}
                className="w-full bg-transparent border border-[var(--border-subtle)] rounded-xl px-4 py-3 text-[var(--text-primary)] placeholder:text-[var(--text-secondary)]/40 focus:outline-none focus:border-[var(--accent-gold)] transition-colors"
            />
        </div>
    );
}
